import { useState, type FormEvent } from "react";
import { emailInvoice, updateInvoice, type UpdateInvoiceInput } from "../../lib/api";
import { formatPriceCents } from "../../lib/money";
import { Modal } from "../../components/Modal";
import {
	ClientContactField,
	type ClientContactFieldValue,
} from "../clients/components/ClientContactField";
import { ScopeEditor } from "../costing/ScopeEditor";
import { hasPricedLineItem, validateScopeDraft, type ScopeDraft } from "../costing/types";
import type { InvoiceRecord } from "../jobs/types";
import { canEditAmounts } from "./lib/invoiceState";
import styles from "./InvoiceEditDialog.module.css";
import type { DocumentListRecord } from "./types";

export interface InvoiceEditDialogProps {
	/** The register row, which is what decides whether amounts are editable. */
	document: DocumentListRecord;
	/** The full invoice — the page already fetched it for the expanded row. */
	invoice: InvoiceRecord;
	onClose: () => void;
	/** Saved cleanly (and re-sent, if that was asked for). */
	onSaved: (message: string) => void;
	/** Saved, but the re-send afterwards did not go out. */
	onEmailFailed: (message: string) => void;
}

const FORM_ID = "invoiceEdit";

function seedScope(invoice: InvoiceRecord): ScopeDraft {
	return {
		description: invoice.scopeDescription ?? "",
		taxRateBps: invoice.taxRateBps,
		// DocumentLineItem is field-identical to LineItemDraft, so a shallow copy
		// per row is the whole adapter — but it must be a copy, or editing the
		// draft would mutate the cached invoice behind the register.
		lineItems: invoice.lineItems.map((item) => ({ ...item })),
	};
}

function formatDay(value: string | null): string {
	return value ? value.slice(0, 10) : "";
}

/**
 * Corrects an existing invoice (DEC-QI-013).
 *
 * Two modes, decided by the same helper the row's action cluster uses:
 * - `full`  — draft, or sent with nothing paid against it. Everything is open.
 * - `notes` — a payment has landed, so the amounts are frozen and the dialog
 *             says so rather than offering fields the server would refuse.
 *
 * Editing a sent invoice asks for confirmation first, because the client may
 * already be holding the number — and the confirmation replaces this dialog's
 * body rather than opening a second Modal: two portalled, focus-trapped shells
 * would fight over the trap and over Escape.
 */
export function InvoiceEditDialog({
	document,
	invoice,
	onClose,
	onSaved,
	onEmailFailed,
}: InvoiceEditDialogProps) {
	const mode = canEditAmounts(document) ? "full" : "notes";
	const isSent = invoice.status === "sent";

	const [scope, setScope] = useState<ScopeDraft>(() => seedScope(invoice));
	const [notes, setNotes] = useState(invoice.notes ?? "");
	const [clientId, setClientId] = useState<string | undefined>(invoice.clientId ?? undefined);
	const [clientName, setClientName] = useState(invoice.clientName ?? "");
	const [clientEmail, setClientEmail] = useState(invoice.clientEmail ?? "");
	const [propertyAddress, setPropertyAddress] = useState(invoice.propertyAddress ?? "");

	const [confirming, setConfirming] = useState(false);
	// An invoice only marked sent has no delivery to repeat; one that was emailed
	// has a copy in someone's inbox that this edit would silently outdate.
	const [resend, setResend] = useState(Boolean(invoice.emailedAt));
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const jobless = invoice.jobId === null;
	const paidCents = document.paidCents ?? 0;

	/** The first thing standing between this form and a valid invoice, or "". */
	const blocker = ((): string => {
		if (mode === "notes") return "";
		if (jobless && !clientId && !clientName.trim()) return "Enter who the invoice is for.";
		if (!hasPricedLineItem(scope)) return "Add at least one priced line item.";
		return validateScopeDraft(scope);
	})();

	const save = async (): Promise<void> => {
		setSubmitting(true);
		setError(null);
		const payload: UpdateInvoiceInput =
			mode === "notes"
				? { notes: notes.trim() }
				: {
						scope,
						notes: notes.trim(),
						...(jobless
							? {
									clientId: clientId ?? "",
									clientName: clientName.trim(),
									clientEmail: clientEmail.trim(),
									propertyAddress: propertyAddress.trim(),
								}
							: {}),
					};

		let saved: InvoiceRecord;
		try {
			({ invoice: saved } = await updateInvoice(invoice.id, payload));
		} catch (saveError) {
			setError(
				saveError instanceof Error ? saveError.message : "Could not save the invoice",
			);
			setSubmitting(false);
			return;
		}

		// From here the edit is committed. A failed re-send is a delivery problem,
		// never a failed save — reporting it as one would have the user change the
		// invoice a second time.
		if (confirming && resend) {
			try {
				const { recipients } = await emailInvoice(invoice.id);
				onSaved(`${saved.number} updated and re-sent to ${recipients.join(", ")}.`);
				return;
			} catch (sendError) {
				onEmailFailed(
					`${saved.number} was updated, but the re-send failed: ${
						sendError instanceof Error ? sendError.message : "unknown error"
					}`,
				);
				onClose();
				return;
			}
		}

		onSaved(`${saved.number} updated.`);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		if (submitting) return;
		if (blocker) {
			setError(blocker);
			return;
		}
		if (mode === "full" && isSent && !confirming) {
			setConfirming(true);
			return;
		}
		void save();
	};

	const handleClientSelect = (client: ClientContactFieldValue | null): void => {
		setClientId(client?.id);
		setClientName(client?.name ?? "");
	};

	const subtitle = [document.clientName || "No client", document.propertyAddress]
		.filter(Boolean)
		.join(" · ");

	if (confirming) {
		return (
			<Modal
				title={`Edit ${invoice.number}`}
				subtitle={subtitle}
				size="sm"
				onClose={onClose}
				footer={
					<>
						<button
							type="button"
							className={styles.secondaryButton}
							onClick={() => setConfirming(false)}
							disabled={submitting}
						>
							Back
						</button>
						<button
							type="button"
							className={styles.primaryButton}
							onClick={() => void save()}
							disabled={submitting}
						>
							{submitting
								? "Saving…"
								: resend
									? "Save and re-send"
									: "Save changes"}
						</button>
					</>
				}
			>
				<p className={styles.confirmCopy}>
					<strong>{invoice.number}</strong> was sent to{" "}
					{document.clientName || "the client"}
					{invoice.sentAt ? ` on ${formatDay(invoice.sentAt)}` : ""}. Saving replaces
					what the PDF shows — including at the link in any email the client already
					has.
				</p>
				<label className={styles.resend}>
					<input
						className={styles.checkbox}
						type="checkbox"
						checked={resend}
						onChange={(event) => setResend(event.currentTarget.checked)}
					/>
					<span>Email the updated invoice to the client after saving</span>
				</label>
				{error && (
					<p className={styles.error} role="alert">
						{error}
					</p>
				)}
			</Modal>
		);
	}

	return (
		<Modal
			title={`Edit ${invoice.number}`}
			subtitle={subtitle}
			size="lg"
			onClose={onClose}
			footer={
				<>
					{blocker && !error && <span className={styles.blocker}>{blocker}</span>}
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={onClose}
						disabled={submitting}
					>
						Cancel
					</button>
					<button
						type="submit"
						form={FORM_ID}
						className={styles.primaryButton}
						disabled={submitting || Boolean(blocker)}
					>
						{submitting ? "Saving…" : "Save changes"}
					</button>
				</>
			}
		>
			<form id={FORM_ID} className={styles.form} onSubmit={handleSubmit} noValidate>
				<fieldset className={styles.fieldset} disabled={submitting}>
					{mode === "notes" ? (
						<>
							<p className={styles.nudge}>
								This invoice has payments recorded against it, so its amounts are
								locked. Notes are printed on the PDF and can still be corrected.
							</p>

							<section className={styles.section}>
								<h3 className={styles.sectionTitle}>Where it stands</h3>
								<dl className={styles.figures}>
									<div>
										<dt className={styles.figureLabel}>Total</dt>
										<dd className={styles.figureValue}>
											{formatPriceCents(invoice.totalCents)}
										</dd>
									</div>
									<div>
										<dt className={styles.figureLabel}>Paid</dt>
										<dd className={styles.figureValue}>
											{formatPriceCents(paidCents)}
										</dd>
									</div>
									<div>
										<dt className={styles.figureLabel}>Balance</dt>
										<dd className={styles.figureValue}>
											{formatPriceCents(invoice.totalCents - paidCents)}
										</dd>
									</div>
								</dl>
								<ul className={styles.frozenItems}>
									{invoice.lineItems.map((item) => (
										<li key={item.id} className={styles.frozenItem}>
											<span>
												{item.title}
												{item.quantity !== 1 ? ` × ${item.quantity}` : ""}
											</span>
											<span className={styles.frozenItemAmount}>
												{formatPriceCents(item.unitAmountCents * item.quantity)}
											</span>
										</li>
									))}
								</ul>
							</section>
						</>
					) : (
						<>
							{isSent && (
								<p className={styles.nudge}>
									Sent {formatDay(invoice.sentAt) || "already"}
									{invoice.emailedAt ? `, emailed ${formatDay(invoice.emailedAt)}` : ""}.
									The client may be holding the version they already have.
								</p>
							)}

							<section className={styles.section}>
								<h3 className={styles.sectionTitle}>Bill to</h3>
								{jobless ? (
									<>
										<ClientContactField
											clientId={clientId}
											clientName={clientName}
											onSelectClient={handleClientSelect}
											onSelectContact={() => undefined}
											showContact={false}
										/>
										<div className={styles.row}>
											<label className={styles.field}>
												<span className={styles.label}>Email</span>
												<input
													className={styles.input}
													name="clientEmail"
													type="email"
													value={clientEmail}
													placeholder="Where to send it"
													onChange={(event) => setClientEmail(event.target.value)}
												/>
											</label>
											<label className={styles.field}>
												<span className={styles.label}>Property or site</span>
												<input
													className={styles.input}
													name="propertyAddress"
													value={propertyAddress}
													placeholder="Where the work was done"
													onChange={(event) =>
														setPropertyAddress(event.target.value)
													}
												/>
											</label>
										</div>
									</>
								) : (
									<p className={styles.hint}>
										Numbered {invoice.number} and billed through job{" "}
										{document.jobRefInternal || "on file"} — the client and property
										come from the job. Change them there.
									</p>
								)}
							</section>

							<ScopeEditor
								value={scope}
								onChange={setScope}
								descriptionTitle="What is being billed"
								descriptionPlaceholder="Describe the work this invoice covers"
								descriptionNote="Printed above the line items as the scope of work."
								itemsEmptyMessage="No line items yet. An invoice needs at least one priced line."
							/>
						</>
					)}

					<section className={styles.section}>
						<h3 className={styles.sectionTitle}>Notes</h3>
						<label className={styles.field}>
							<span className={styles.label}>Notes on the invoice</span>
							<textarea
								className={styles.textarea}
								name="notes"
								rows={3}
								value={notes}
								placeholder="Anything the client should read with this invoice"
								onChange={(event) => setNotes(event.target.value)}
							/>
							<span className={styles.note}>
								Payment terms and bank details come from Documents settings — they do
								not belong here.
							</span>
						</label>
					</section>
				</fieldset>

				{error && (
					<p className={styles.error} role="alert">
						{error}
					</p>
				)}
			</form>
		</Modal>
	);
}
